import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  AppButton,
  Box,
  Controller,
  FieldWrapper,
  FormProvider,
  Label,
  Preloader,
  RegularField,
  SelectDropdown,
  Text,
} from '@common-fe/common-fe';

import { Modal } from '@/components';

import { SERVICE_TYPE_LABELS } from '../../../Services.constants';
import { ServiceTypeValues } from '../../../Services.types';
import { CATEGORY_DROPDOWN_MAX_HEIGHT, SERVICE_MODAL_MODE } from '../ServiceModal.constants';
import { CreateServiceFormValues } from '../ServiceModal.types';

interface ServiceModalProps {
  visible: boolean;
  mode: string;
  methods: UseFormReturn<CreateServiceFormValues>;
  categoryOptions: { key: string; value: string }[];
  isLoading: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

const ServiceModal: React.FC<ServiceModalProps> = ({
  visible,
  mode,
  methods,
  categoryOptions,
  isLoading,
  onClose,
  onSubmit,
}) => {
  const {
    control,
    formState: { errors },
  } = methods;

  const isEditMode = mode === SERVICE_MODAL_MODE.EDIT;
  const title = isEditMode ? 'Edit Service' : 'Create a Service';
  const submitButtonText = isEditMode ? 'Save' : 'Create';
  const testIdPrefix = isEditMode ? 'edit-service-modal' : 'create-service-modal';

  return (
    <Modal visible={visible} onSetVisible={onClose}>
      <FormProvider {...methods}>
        <Box direction="column" gap="spacing24">
          <Box direction="row" align="center" justify="center">
            <Text size="2xl" color="textTitle" weight="bold">
              {title}
            </Text>
          </Box>
          <Box background="module" round="moduleRound" pad="spacing24">
            <Box
              background="canvas"
              border={{ size: 'small', color: 'border2' }}
              round="container1Round"
              pad="spacing24"
              gap="spacing16"
            >
              <FieldWrapper align="center" style={{ justifyContent: 'space-between' }}>
                <Label showRequireIcon name="name" isNarrow>
                  Service name
                </Label>
                <Box width="320px">
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <RegularField
                        {...field}
                        testId={`${testIdPrefix}_name-input`}
                        value={field.value ?? ''}
                        placeholder="Service name"
                        errorText={errors.name?.message}
                        editMode
                      />
                    )}
                  />
                </Box>
              </FieldWrapper>
              <FieldWrapper align="center" style={{ justifyContent: 'space-between' }}>
                <Label showRequireIcon name="categoryType" isNarrow>
                  Service category
                </Label>
                <Box width="320px">
                  <Controller
                    name="categoryType"
                    control={control}
                    render={({ field }) => (
                      <SelectDropdown
                        testId={`${testIdPrefix}_category-dropdown`}
                        options={categoryOptions}
                        value={field.value}
                        onChange={(value) => field.onChange(value)}
                        placeholder="Select category"
                        singleMode
                        id="categoryType"
                        name="categoryType"
                        dropdownStyles={{ maxHeight: CATEGORY_DROPDOWN_MAX_HEIGHT }}
                        errorText={errors.categoryType?.message}
                      />
                    )}
                  />
                </Box>
              </FieldWrapper>
              <FieldWrapper align="center" style={{ justifyContent: 'space-between' }}>
                <Label name="type" isNarrow>
                  Type
                </Label>
                <Box width="320px">
                  <Text size="medium" color="textBody">
                    {SERVICE_TYPE_LABELS[ServiceTypeValues.MANUAL_CLAIM]}
                  </Text>
                </Box>
              </FieldWrapper>
            </Box>
          </Box>
          <Box direction="row" align="center" justify="end" gap="s">
            <AppButton
              testId={`${testIdPrefix}_cancel-button`}
              buttonType="secondary"
              width="control"
              onClick={onClose}
            >
              Cancel
            </AppButton>
            <AppButton
              testId={`${testIdPrefix}_submit-button`}
              width="control"
              onClick={onSubmit}
              disabled={isLoading}
            >
              {isLoading ? <Preloader color="white" /> : <Text>{submitButtonText}</Text>}
            </AppButton>
          </Box>
        </Box>
      </FormProvider>
    </Modal>
  );
};

export default ServiceModal;
